/**
 * AdminLocationIntelligence — Location Intelligence Quality Dashboard
 * Provides visibility into the Jamaica Location Intelligence Layer.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  ArrowLeft, RefreshCw, MapPin, Search, AlertTriangle, CheckCircle,
  TrendingUp, Loader2, Shield, Target, Clock, Hash, Zap, AlertCircle,
} from 'lucide-react';

const confColor = { trusted: 'bg-emerald-100 text-emerald-700', highly_confirmed: 'bg-blue-100 text-blue-700', confirmed: 'bg-amber-100 text-amber-700', suggested: 'bg-gray-100 text-gray-600' };

export default function AdminLocationIntelligence() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decaying, setDecaying] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/location-intelligence/admin/quality');
      setData(res.data.report);
    } catch (e) {
      toast.error('Failed to load dashboard');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runDecay = async (dryRun) => {
    setDecaying(true);
    try {
      const res = await api.post(`/location-intelligence/admin/decay?dry_run=${dryRun}`);
      const r = res.data.result;
      if (dryRun) {
        toast.info(`Decay preview: ${r.candidates} locations would be demoted`);
      } else {
        toast.success(`Decay applied: ${r.candidates} locations demoted`);
        fetchData();
      }
    } catch (e) {
      toast.error('Decay failed');
    }
    setDecaying(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
    </div>
  );

  const o = data?.overview || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-location-intelligence">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-200 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900">Location Intelligence</h1>
            <p className="text-sm text-gray-500">Jamaica address quality & learning metrics</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Overview cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="overview-cards">
          <Card className="bg-white border border-slate-200 text-slate-900"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{o.total_learned_locations || 0}</p>
            <p className="text-xs text-gray-500">Learned locations</p>
          </CardContent></Card>
          <Card className="bg-white border border-slate-200 text-slate-900"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{o.total_aliases || 0}</p>
            <p className="text-xs text-gray-500">Aliases</p>
          </CardContent></Card>
          <Card className="bg-white border border-slate-200 text-slate-900"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{o.total_searches || 0}</p>
            <p className="text-xs text-gray-500">Searches</p>
          </CardContent></Card>
          <Card className="bg-white border border-slate-200 text-slate-900"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{o.booking_completion_rate || 0}%</p>
            <p className="text-xs text-gray-500">Booking rate</p>
          </CardContent></Card>
        </div>

        {/* Confidence breakdown */}
        <div className="flex gap-2 flex-wrap mb-6" data-testid="confidence-breakdown">
          {Object.entries(o.confidence_breakdown || {}).map(([level, count]) => (
            <Badge key={level} className={`${confColor[level] || 'bg-gray-100 text-gray-600'} px-3 py-1 text-sm`}>
              {level}: {count}
            </Badge>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="top" className="space-y-4">
          <TabsList className="grid grid-cols-3 md:grid-cols-6 gap-1">
            <TabsTrigger value="top"><TrendingUp className="w-3 h-3 mr-1" />Top</TabsTrigger>
            <TabsTrigger value="corrections"><Target className="w-3 h-3 mr-1" />Corrections</TabsTrigger>
            <TabsTrigger value="problems"><AlertTriangle className="w-3 h-3 mr-1" />Problems</TabsTrigger>
            <TabsTrigger value="search"><Search className="w-3 h-3 mr-1" />Search</TabsTrigger>
            <TabsTrigger value="aliases"><Hash className="w-3 h-3 mr-1" />Aliases</TabsTrigger>
            <TabsTrigger value="decay"><Clock className="w-3 h-3 mr-1" />Decay</TabsTrigger>
          </TabsList>

          {/* Top Locations */}
          <TabsContent value="top">
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="bg-white border border-slate-200 text-slate-900">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-amber-500" />Most Used</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                    {(data?.top_locations || []).map((loc, i) => (
                      <div key={i} className="px-4 py-2.5 flex items-center gap-3">
                        <span className="text-xs text-gray-400 w-5">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{loc.display_name}</p>
                          <p className="text-xs text-gray-500">{loc.parish}</p>
                        </div>
                        <Badge className={confColor[loc.confidence] || 'bg-gray-100'}>{loc.confidence}</Badge>
                        <span className="text-sm font-bold text-gray-700">{loc.usage_count}x</span>
                      </div>
                    ))}
                    {(data?.top_locations || []).length === 0 && <p className="text-sm text-gray-400 p-4 text-center">No usage data yet</p>}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border border-slate-200 text-slate-900">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-1.5"><Shield className="w-4 h-4 text-emerald-500" />Trusted & Highly Confirmed</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                    {(data?.trusted_locations || []).map((loc, i) => (
                      <div key={i} className="px-4 py-2.5 flex items-center gap-3">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{loc.display_name}</p>
                          <p className="text-xs text-gray-500">{loc.parish} | {loc.successful_trip_count} trips</p>
                        </div>
                        <Badge className={confColor[loc.confidence]}>{loc.confidence}</Badge>
                      </div>
                    ))}
                    {(data?.trusted_locations || []).length === 0 && <p className="text-sm text-gray-400 p-4 text-center">No trusted locations yet — requires 10+ successful trips</p>}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Correction Insights */}
          <TabsContent value="corrections">
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="bg-white border border-slate-200 text-slate-900">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-1.5"><Target className="w-4 h-4 text-amber-500" />Most Corrected</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                    {(data?.correction_insights?.most_corrected || []).map((loc, i) => (
                      <div key={i} className="px-4 py-2.5 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{loc.display_name}</p>
                          <p className="text-xs text-gray-500">{loc.parish}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-amber-600">{Math.round(loc.avg_correction_distance_m)}m avg</p>
                          <p className="text-xs text-gray-400">{loc.correction_count} corrections</p>
                        </div>
                      </div>
                    ))}
                    {(data?.correction_insights?.most_corrected || []).length === 0 && <p className="text-sm text-gray-400 p-4 text-center">No corrections recorded yet</p>}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border border-slate-200 text-slate-900">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-1.5"><AlertCircle className="w-4 h-4 text-red-500" />Flagged Locations</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                    {(data?.correction_insights?.flagged_locations || []).map((loc, i) => (
                      <div key={i} className="px-4 py-2.5 flex items-center gap-3">
                        <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{loc.display_name}</p>
                          <p className="text-xs text-gray-500">{loc.parish} | {Math.round(loc.avg_correction_distance_m)}m avg correction</p>
                        </div>
                      </div>
                    ))}
                    {(data?.correction_insights?.flagged_locations || []).length === 0 && <p className="text-sm text-gray-400 p-4 text-center">No flagged locations</p>}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Problem Locations */}
          <TabsContent value="problems">
            <Card className="bg-white border border-slate-200 text-slate-900">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-red-500" />Problem Locations</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                  {(data?.problem_locations || []).map((loc, i) => (
                    <div key={i} className="px-4 py-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{loc.display_name}</p>
                        <p className="text-xs text-gray-500">{loc.parish}</p>
                      </div>
                      <div className="text-right space-y-0.5">
                        <p className="text-sm font-bold text-red-600">{loc.failure_rate}% fail</p>
                        <p className="text-xs text-gray-400">{loc.failed_trip_count} failed / {loc.successful_trip_count} success</p>
                        {loc.avg_correction_distance_m > 0 && <p className="text-xs text-amber-500">{Math.round(loc.avg_correction_distance_m)}m avg correction</p>}
                      </div>
                    </div>
                  ))}
                  {(data?.problem_locations || []).length === 0 && <p className="text-sm text-gray-400 p-6 text-center">No problem locations detected</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Search Insights */}
          <TabsContent value="search">
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="bg-white border border-slate-200 text-slate-900">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-1.5"><Search className="w-4 h-4 text-blue-500" />Frequent Queries</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                    {(data?.search_insights?.frequent_queries || []).map((q, i) => (
                      <div key={i} className="px-4 py-2 flex items-center gap-3">
                        <span className="text-xs text-gray-400 w-5">{i + 1}</span>
                        <p className="text-sm text-gray-900 flex-1 truncate">{q.query}</p>
                        <span className="text-sm font-bold text-gray-600">{q.count}x</span>
                      </div>
                    ))}
                    {(data?.search_insights?.frequent_queries || []).length === 0 && <p className="text-sm text-gray-400 p-4 text-center">No search data yet</p>}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border border-slate-200 text-slate-900">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-1.5"><Target className="w-4 h-4 text-amber-500" />Queries Needing Correction</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                    {(data?.search_insights?.correction_queries || []).map((q, i) => (
                      <div key={i} className="px-4 py-2 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 truncate">{q.query}</p>
                        </div>
                        <span className="text-xs text-amber-600 font-medium">{q.avg_correction_m}m avg</span>
                        <span className="text-sm font-bold text-gray-600">{q.count}x</span>
                      </div>
                    ))}
                    {(data?.search_insights?.correction_queries || []).length === 0 && <p className="text-sm text-gray-400 p-4 text-center">No correction-heavy queries yet</p>}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Alias Insights */}
          <TabsContent value="aliases">
            <Card className="bg-white border border-slate-200 text-slate-900">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-1.5"><Hash className="w-4 h-4 text-purple-500" />Recent Aliases ({data?.alias_insights?.total_aliases || 0} total)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                  {(data?.alias_insights?.recent_aliases || []).map((a, i) => (
                    <div key={i} className="px-4 py-2 flex items-center gap-3">
                      <Zap className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                      <p className="text-sm font-medium text-gray-900 flex-1">{a.alias}</p>
                      <p className="text-xs text-gray-400 font-mono">{a.normalized_alias}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Confidence Decay */}
          <TabsContent value="decay">
            <Card className="bg-white border border-slate-200 text-slate-900">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-1.5"><Clock className="w-4 h-4 text-orange-500" />Confidence Decay</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">Locations unused for 90+ days are candidates for confidence demotion. Seeded locations are exempt.</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => runDecay(true)} disabled={decaying}>
                    {decaying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Preview decay
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => runDecay(false)} disabled={decaying}>
                    Apply decay
                  </Button>
                </div>
                {/* Stale locations */}
                {(data?.stale_locations || []).length > 0 && (
                  <div className="border rounded-lg divide-y divide-gray-100">
                    <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500">Stale locations (90+ days unused)</div>
                    {data.stale_locations.map((loc, i) => (
                      <div key={i} className="px-4 py-2 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 truncate">{loc.display_name}</p>
                          <p className="text-xs text-gray-500">{loc.parish}</p>
                        </div>
                        <Badge className={confColor[loc.confidence]}>{loc.confidence}</Badge>
                        {loc.days_stale && <span className="text-xs text-orange-600 font-medium">{loc.days_stale}d stale</span>}
                      </div>
                    ))}
                  </div>
                )}
                {(data?.stale_locations || []).length === 0 && <p className="text-sm text-gray-400">No stale locations — all locations are recently active</p>}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
